/**
 * Crop viewport state machine for EntityImageEditor.
 *
 * One rule governs everything here: the crop `area` (natural media pixels) is the model,
 * and the on-screen view (`cropFrameSize`, `zoom`, `crop`) is always derived from it.
 *
 * | Event                        | Effect                                   |
 * |------------------------------|------------------------------------------|
 * | Canvas resized               | area preserved, view re-derived          |
 * | Aspect / shape changed       | view preserved, area recomputed          |
 * | Drag, pinch, zoom, rotation  | view changed, area recomputed            |
 *
 * No path depends on timing: react-easy-crop emits intermediate values while it reflows,
 * and every one of them is ignored (see `gestureRef`).
 *
 * See docs/specs/transversal/editeur-images-unifie.md §6 before changing this file.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  areaFromCoverage,
  areaFromView,
  clampCoverage,
  computeFrameSize,
  computeMediaSize,
  coverageFromArea,
  defaultArea,
  rotateSize,
  viewFromArea,
} from './entityImageCropView';
import {
  normalizeRotationDegrees,
  rotateCropAroundCanvasCenter,
  shortestRotationDelta,
} from './entityImageCanvasPivot';

const ZOOM_WHEEL_SPEED = 400;

/**
 * @param {{ aspect: number, active: boolean }} params
 *  - `aspect`: crop frame ratio (width/height), already resolved from shape + format.
 *  - `active`: the cropper is mounted and should track its canvas.
 */
export function useCropViewport({ aspect, active }) {
  const canvasRef = useRef(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  /** Source of truth: crop rect in natural media pixels (rotated bbox space). */
  const [cropArea, setCropArea] = useState(null);
  /** Visible cropper canvas, measured with sub-pixel precision. */
  const [containerSize, setContainerSize] = useState(null);
  /** Natural media size — the only Cropper metric we keep, it never changes. */
  const [naturalSize, setNaturalSize] = useState(null);

  const cropRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const rotationRef = useRef(0);
  const cropAreaRef = useRef(null);
  /** True only between react-easy-crop's interaction start/end, i.e. real user gestures. */
  const gestureRef = useRef(false);

  /** Rendered photo size — recomputed on every canvas resize, never cached. */
  const mediaSize = useMemo(
    () => computeMediaSize(naturalSize, containerSize),
    [naturalSize, containerSize],
  );
  /** Crop frame: always refitted to the canvas, so it keeps its margins to the edges. */
  const cropFrameSize = useMemo(
    () => computeFrameSize(containerSize, aspect),
    [containerSize, aspect],
  );

  const applyView = useCallback((view) => {
    if (!view) {
      return;
    }
    cropRef.current = view.crop;
    zoomRef.current = view.zoom;
    setCrop(view.crop);
    setZoom(view.zoom);
  }, []);

  const applyArea = useCallback((area) => {
    if (!area?.width || !area?.height) {
      return;
    }
    cropAreaRef.current = area;
    setCropArea(area);
  }, []);

  /** Area changed: the framing is the model, so the view is re-derived from it. */
  const commitArea = useCallback(
    (area) => {
      if (!area?.width || !area?.height) {
        return;
      }
      applyArea(area);
      applyView(
        viewFromArea({
          area,
          mediaSize,
          cropSize: cropFrameSize,
          rotation: rotationRef.current,
        }),
      );
    },
    [applyArea, applyView, cropFrameSize, mediaSize],
  );

  /** View changed: the photo moved on screen, so the area under the frame is recomputed. */
  const commitView = useCallback(
    (view, { rotation: viewRotation = rotationRef.current, cropSize = cropFrameSize } = {}) => {
      if (!view) {
        return;
      }
      applyView(view);
      applyArea(
        areaFromView({
          crop: view.crop,
          zoom: view.zoom,
          mediaSize,
          cropSize,
          rotation: viewRotation,
        }),
      );
    },
    [applyArea, applyView, cropFrameSize, mediaSize],
  );

  // Measure the cropper canvas (panel open/close, drag, window resize) in fractional
  // pixels: rounding here would leak into the frame aspect and the exported crop.
  useLayoutEffect(() => {
    const node = canvasRef.current;
    if (!active || !node) {
      return undefined;
    }

    const measure = () => {
      const { width, height } = node.getBoundingClientRect();
      setContainerSize((prev) => {
        if (prev && Math.abs(prev.width - width) < 0.5 && Math.abs(prev.height - height) < 0.5) {
          return prev;
        }
        return { width, height };
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [active]);

  // Canvas resized (or media loaded): the crop area is preserved and the view — frame,
  // zoom, translation — is re-derived from it. This is what keeps the framing identical.
  useLayoutEffect(() => {
    if (!active || !mediaSize || !cropFrameSize) {
      return;
    }

    let area = cropAreaRef.current;
    if (!area?.width || !area?.height) {
      area = defaultArea(naturalSize, aspect, rotationRef.current);
      if (!area) {
        return;
      }
      cropAreaRef.current = area;
      setCropArea(area);
    }

    applyView(
      viewFromArea({ area, mediaSize, cropSize: cropFrameSize, rotation: rotationRef.current }),
    );
  }, [active, mediaSize, cropFrameSize, naturalSize, aspect, rotation, applyView]);

  const onMediaLoaded = useCallback(({ naturalWidth, naturalHeight }) => {
    setNaturalSize({ width: naturalWidth, height: naturalHeight });
  }, []);

  const onInteractionStart = useCallback(() => {
    gestureRef.current = true;
  }, []);

  const onInteractionEnd = useCallback(() => {
    gestureRef.current = false;
  }, []);

  const onCropChange = useCallback(
    (nextCrop) => {
      // Outside a gesture this is react-easy-crop realigning itself after a reflow:
      // our derived view is authoritative, so ignore it.
      if (!gestureRef.current) {
        return;
      }
      commitView({ crop: nextCrop, zoom: zoomRef.current });
    },
    [commitView],
  );

  const onZoomChange = useCallback(
    (nextZoom) => {
      if (!gestureRef.current || !Number.isFinite(nextZoom)) {
        return;
      }
      commitView({ crop: cropRef.current, zoom: nextZoom });
    },
    [commitView],
  );

  /** Zoom pivoted on the frame center: scaling the area around its own center. */
  const setCoverage = useCallback(
    (nextCoverage) => {
      const area = cropAreaRef.current;
      if (!area || !naturalSize) {
        return;
      }
      commitArea(areaFromCoverage(area, nextCoverage, naturalSize, aspect, rotationRef.current));
    },
    [aspect, commitArea, naturalSize],
  );

  /** Apply a rotation delta while keeping the canvas-center media point fixed. */
  const rotateBy = useCallback(
    (deltaDegrees) => {
      if (!Number.isFinite(deltaDegrees) || deltaDegrees === 0) {
        return;
      }
      // Keep the same continuous domain as react-easy-crop gesture updates (may exceed 0–359).
      const nextRotation = rotationRef.current + deltaDegrees;
      const nextCrop = rotateCropAroundCanvasCenter(cropRef.current, deltaDegrees);
      rotationRef.current = nextRotation;
      setRotation(nextRotation);
      commitView({ crop: nextCrop, zoom: zoomRef.current }, { rotation: nextRotation });
    },
    [commitView],
  );

  const onRotationChange = useCallback(
    (nextRotation) => {
      if (!Number.isFinite(nextRotation)) {
        return;
      }
      rotateBy(nextRotation - rotationRef.current);
    },
    [rotateBy],
  );

  /** Rotation slider: take the short path across the 0° boundary. */
  const rotateTo = useCallback(
    (nextRotation) => {
      rotateBy(
        shortestRotationDelta(
          normalizeRotationDegrees(rotationRef.current),
          normalizeRotationDegrees(nextRotation),
        ),
      );
    },
    [rotateBy],
  );

  /**
   * Call before switching shape or format: the photo must not move, so the area is
   * recomputed under the frame the new aspect will produce.
   */
  const prepareAspectChange = useCallback(
    (nextAspect) => {
      const nextFrame = computeFrameSize(containerSize, nextAspect);
      if (!nextFrame || !mediaSize) {
        return;
      }
      applyArea(
        areaFromView({
          crop: cropRef.current,
          zoom: zoomRef.current,
          mediaSize,
          cropSize: nextFrame,
          rotation: rotationRef.current,
        }),
      );
    },
    [applyArea, containerSize, mediaSize],
  );

  /** Move the crop center along one axis, in half-widths of the photo. */
  const setPan = useCallback(
    (axis, value) => {
      const area = cropAreaRef.current;
      if (!area || !naturalSize || !Number.isFinite(value)) {
        return;
      }
      const bbox = rotateSize(naturalSize.width, naturalSize.height, rotationRef.current);
      const half = axis === 'x' ? bbox.width / 2 : bbox.height / 2;
      const size = axis === 'x' ? area.width : area.height;
      commitArea({ ...area, [axis]: Math.round(half + value * half - size / 2) });
    },
    [commitArea, naturalSize],
  );

  // Wheel zoom stays pivoted on the frame center (react-easy-crop's own wheel handler is
  // disabled through `onWheelRequest`, which zooms on the cursor instead).
  useEffect(() => {
    const node = canvasRef.current;
    if (!node || !active) {
      return undefined;
    }

    const onWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const factor = 1 - event.deltaY / ZOOM_WHEEL_SPEED;
      const coverage = coverageFromArea(cropAreaRef.current, naturalSize, rotationRef.current);
      if (!coverage || !Number.isFinite(factor) || factor <= 0) {
        return;
      }
      setCoverage(coverage / factor);
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [active, naturalSize, setCoverage]);

  /** Restore a persisted crop (DB metadata) before the media is even loaded. */
  const restore = useCallback(({ area, rotation: nextRotation = 0 }) => {
    cropAreaRef.current = area?.width && area?.height ? area : null;
    setCropArea(cropAreaRef.current);
    rotationRef.current = nextRotation;
    setRotation(nextRotation);
  }, []);

  /** Forget the current photo: next media load starts from a centered default crop. */
  const reset = useCallback(({ keepCanvas = false } = {}) => {
    cropRef.current = { x: 0, y: 0 };
    zoomRef.current = 1;
    rotationRef.current = 0;
    cropAreaRef.current = null;
    gestureRef.current = false;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCropArea(null);
    setNaturalSize(null);
    if (!keepCanvas) {
      setContainerSize(null);
    }
  }, []);

  // Slider values live in the area domain, so they stay stable when the canvas resizes.
  const zoomFactor = 1 / clampCoverage(coverageFromArea(cropArea, naturalSize, rotation) ?? 1);
  const pan = useMemo(() => {
    if (!cropArea || !naturalSize) {
      return { x: 0, y: 0 };
    }
    const bbox = rotateSize(naturalSize.width, naturalSize.height, rotation);
    return {
      x: (cropArea.x + cropArea.width / 2) / (bbox.width / 2) - 1,
      y: (cropArea.y + cropArea.height / 2) / (bbox.height / 2) - 1,
    };
  }, [cropArea, naturalSize, rotation]);

  return {
    canvasRef,
    crop,
    zoom,
    rotation,
    cropArea,
    cropFrameSize,
    zoomFactor,
    pan,
    onMediaLoaded,
    onCropChange,
    onZoomChange,
    onRotationChange,
    onInteractionStart,
    onInteractionEnd,
    setCoverage,
    setPan,
    rotateBy,
    rotateTo,
    prepareAspectChange,
    restore,
    reset,
  };
}

export default useCropViewport;
